import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, ViewChild } from '@angular/core';
import { filter, mergeMap, of, Subscription, take } from 'rxjs';

import { MessageService } from 'primeng/api';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { validateEmails } from '@Common/Misc/helpers/formatting/email.helper';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { BriefingModel } from '@Monitor/Briefings/models/briefing.model';
import { deliveryOptionsEnum } from '@Monitor/Shared/enums/delivery-options.enum';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { copy } from '@Common/Misc/helpers/copy/copy.helper';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { MailingListsService } from '@Common/MailingLists/services/mailing-lists.service';
import { StepsBannerErrorConfigModel } from '@Common/Misc/models/banner-error-config.model';
import { bannerErrorsMap, BriefingBannerErrorTypeEnum } from '@Monitor/Briefings/enums/briefing-banner-error-type.enum';
import { ConfigService } from '@Core/Config/services/config.service';
import { ResizeDirectionEnum } from '../../../../Common/Forms/components/TextareaInput/textarea-input.component';
import { deliverySchedulingTypesEnum } from '../../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { BriefingRecurrencePatternModel } from '../../models/briefing-recurrence-pattern.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { TimeModel } from '../../../../Common/Forms/models/time.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TimeHelper } from '../../../../Common/Forms/helpers/time.helper';
import { DeliverySchedulerComponent } from '../../../../Common/Forms/components/DeliveryScheduler/delivery-scheduler.component';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { EmailDesignStyleEnum } from '../CanvasEmailStyle/canvas-email-style.component';
import { OnlineBriefingInviteStyleModel } from '@api/briefings/model/briefing-invite-style.model';
import { TemplateApiService } from '@api/templates/template.api.service';
import { EmailPreviewDialogComponent } from '../EmailPreviewDialog/email-preview-dialog.component';


@Component({
    selector: 'agpr-briefing-publishing-step',
    templateUrl: './briefing-publishing-step.component.html',
    styleUrls: ['./briefing-publishing-step.component.scss'],
    standalone: false
})
export class BriefingPublishingStepComponent implements OnInit, OnDestroy {
    readonly id = 'briefing_publishing_step';
    readonly deliveryOptionsEnum = deliveryOptionsEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly maxInputTextLength: number = 256;
    readonly deliveryTypes = deliverySchedulingTypesEnum;
    readonly validateEmails: (string) => boolean = validateEmails;
    readonly deliveryMethods = BriefingDeliveryMethodEnum;

    @Input() reservedUrl: string;
    @Input() draftPublished: boolean;
    @Input() showErrors: boolean;
    @Input() publishDraftInProgress: boolean = false;
    @Input() requireAutomaticInsightGen: boolean = false;
    @Input() deliverySchedulerInputFields;
    @Input() cachedSchedulingType: deliverySchedulingTypesEnum;

    @Output() changes: EventEmitter<null> = new EventEmitter();
    @Output() publishDraft: EventEmitter<null> = new EventEmitter();
    @Output() publishingErrors: EventEmitter<StepsBannerErrorConfigModel[]> = new EventEmitter();

    @ViewChild('deliveryScheduler') deliveryScheduler: DeliverySchedulerComponent;

    briefing: BriefingModel;
    replyToInvalid: boolean = false;
    mailingListError: boolean = false;

    subscriptions: Subscription[] = [];

    hasMentionInsightsEnabled: boolean;
    isRefMediaAccount: boolean = false;
    preferences: PreferencesModel;
    cachedSendEmpty: boolean = null;
    initialEmailDesignType: EmailDesignStyleEnum;
    brandStyle: OnlineBriefingInviteStyleModel | null = null;

    constructor(
        public briefingService: BriefingsService,
        public currentUserStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        public briefingsService: BriefingsService,
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public mailingListsService: MailingListsService,
        public configService: ConfigService,
        public timeHelper: TimeHelper,
        public templateApiService: TemplateApiService,
    ) {
        this.isRefMediaAccount = this.configService.brandGUIDIsRefMedia();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.briefingService.getFocusedResource().pipe(filter(x => !!x)).subscribe(briefing => {
                this.briefing = briefing;
                setTimeout(() => {
                    this.brandStyle = this.buildBrandStyle(briefing);
                }, 0);
                if (!this.initialEmailDesignType) {
                    this.initialEmailDesignType = briefing.onlineBriefingInviteStyle ? EmailDesignStyleEnum.CUSTOM : EmailDesignStyleEnum.DEFAULT;
                }
                this.setErrorList();
            }),
            this.monitorConfigService.getHasMentionInsightsEnabled().subscribe(x => this.hasMentionInsightsEnabled = x),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    if (preferences) {
                        this.preferences = preferences;
                    }
                }
            ),
        );
    }

    public deliveryOptionChanged(selected: boolean, value: deliveryOptionsEnum) {
        this.briefing.deliveryOptionChanged(selected, value);
        if (!this.briefing?.selectedDeliveryOption?.rssFeed) {
            this.briefing.feeds = [];
        }
        if (!this.briefing?.selectedDeliveryOption?.email)  {
            this.briefing.includeInsightsLink = false;
        }

        if (!this.briefing?.selectedDeliveryOption?.digest) {
            this.briefing.digests = [];
        }

        // we only want to unselect includeInsightsLink when we deselect email
        if (!selected && (value === deliveryOptionsEnum.email)) {
            this.briefing.includeInsightsLink = false;
            this.insightLinkChanged();
        } else {
            this.briefingChanged();
        }

    }

    replyToChanged(): void {
        if (this.briefing.replyTo?.trim() !== '') {
            this.replyToInvalid = !validateEmails(this.briefing.replyTo);
        }
        this.briefingChanged();
    }

    insightLinkChanged() {
        this.briefingsService.updateIncludeInsightsLink();
        this.briefingChanged();
    }

    setErrorList() {
        const errors = [];

        // subject line is check for Canvas
        if (this.briefing.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
            if (!this.briefing?.subject || this.briefing?.subject.trim() === '') {
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_SUBJECT_ERROR));
            }
        }

        if (this.briefing?.selectedDeliveryOption?.email) {
            if (!this.briefing?.subject || this.briefing?.subject.trim() === '') {
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_SUBJECT_ERROR));
            }

            this.mailingListError = false;
            if (this.briefing?.mailingListIds?.length && this.mailingListsService.getRecipientCount(this.briefing?.mailingListIds) === 0) {
                this.mailingListError = true;
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_MAILING_LIST_ERROR));
            }

            // total recipient count is not set for Canvas
            if (this.briefing.deliveryMethod !== BriefingDeliveryMethodEnum.ONLINE) {
                if (this.briefing?.mailingListIds?.length === 0 && (!this.briefing.recipientList || this.briefing?.recipientList?.length === 0)) {
                    errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_RECIPIENT_ERROR));
                }
            }

            if (!this.briefing?.fromName || this.briefing?.fromName.trim() === '') {
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_FROMNAME_ERROR));
            }
            this.replyToInvalid = !validateEmails(this.briefing.replyTo);

            if (!this.briefing?.replyTo || this.briefing?.replyTo.trim() === '' || this.replyToInvalid) {
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_REPLYTO_ERROR));
            }
        }

        if (this.briefing?.selectedDeliveryOption?.rssFeed) {
            if (this.briefing.feeds.length === 0) {
                errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_RSS_FEED_ERROR));
            }
        }

        if (!this.briefing?.selectedDeliveryOption?.email && !this.briefing?.selectedDeliveryOption?.rssFeed) {
            errors.push(bannerErrorsMap.get(BriefingBannerErrorTypeEnum.BRIEFING_DELIVERY_OPTIONS_ERROR));
        }

        this.publishingErrors.emit(errors);
    }

    setBriefing() {
        this.briefingsService.setCurrentBriefing(this.briefing);
    }

    briefingChanged() {
        this.setErrorList();
        this.changes.emit();
    }

    includeEmptyMentionSectionsChanged() {
        this.briefingsService.setIncludeEmptyMentionSections(this.briefing.includeEmptyMentionSections);
        this.briefingChanged();
    }

    copyUrl(event: Event, url: string): void {
        if (!url) {
            return;
        }
        copy.text(url);
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.summary'),
            detail: this.localizationService.transform('Briefings.reservedUrl.noUrlAvailable.message.detail'),
        });
        (event.target as HTMLElement).focus();
    }

    /**
     * Event handler for when the delivery component emits the recurringPatternChanged event, updates recurring pattern accordingly
     */
    public updateRecurringPattern(event: BriefingRecurrencePatternModel): void {
        this.briefing.sendAt.setRecurring(event);
        // Trigger dirty checking
        this.briefingChanged();
    }

    /**
     * Event handler for when the delivery component emits the schedulingTypeChanged component, updates sentAt object accordingly
     */
    public selectedSchedulingTypeChanged(event: deliverySchedulingTypesEnum): void {
        switch (event) {
            case deliverySchedulingTypesEnum.immediate:
                this.briefing.sendAt.setSendNow();
                this.deliverySchedulerInputFields.deliveryTime = {
                    format: this.preferences.timeFormat,
                    hour: null,
                    minute: null,
                    timeZone: this.briefing.sendAt.timeZone
                };
                this.cachedSendEmpty = this.briefing.sendRecurringBriefingWhenMentionsEmpty;
                this.briefing.sendRecurringBriefingWhenMentionsEmpty = true;
                this.cachedSchedulingType = event;
                this.briefingChanged();
                break;
            case deliverySchedulingTypesEnum.future:
                this.briefing.sendAt.setSendLater(this.deliverySchedulerInputFields.deliveryDate);
                this.deliverySchedulerInputFields.deliveryTime = this.timeHelper.getTimeFromDate(
                    this.deliverySchedulerInputFields.deliveryDate,
                    this.briefing.sendAt.timeZone,
                    this.preferences.timeFormat
                );
                this.cachedSendEmpty = this.briefing.sendRecurringBriefingWhenMentionsEmpty;
                this.briefing.sendRecurringBriefingWhenMentionsEmpty = true;
                this.cachedSchedulingType = event;
                this.briefingChanged();
                break;
            case deliverySchedulingTypesEnum.recurring:
                of(this.draftPublished).pipe(
                    mergeMap(draftPublished => {
                        if (draftPublished && this.cachedSchedulingType !== deliverySchedulingTypesEnum.recurring) {
                            const io: ConfirmDialogModel = {
                                inputs: {
                                    id: 'briefing.switchToRecurring.confirmationDialog',
                                    heading: 'Briefings.reservedUrl.publishedDrafts',
                                    message: 'Briefings.reservedUrl.changeToRecurring',
                                    okLabel: 'Briefings.reservedUrl.changeToRecurring.continue'
                                },
                                outputs: {
                                    dialogClose: (confirmed) => {}
                                }
                            };
                            return this.coreDialogService.openWithObservable(ConfirmDialogComponent, io);
                        } else {
                            return of(true);
                        }
                    } ),
                    take(1)
                ).subscribe(confirm => {
                    if (confirm) {
                        this.briefing.sendAt.setRecurring(this.deliverySchedulerInputFields.recurrenceModel);
                        this.deliverySchedulerInputFields.deliveryTime = {
                            format: this.preferences.timeFormat,
                            hour: null,
                            minute: null,
                            timeZone: this.briefing.sendAt.timeZone
                        };
                        if (this.cachedSendEmpty !== null) {
                            this.briefing.sendRecurringBriefingWhenMentionsEmpty = this.cachedSendEmpty;
                        }
                        this.cachedSchedulingType = this.deliverySchedulerInputFields.schedulingType;
                    } else {
                        this.deliverySchedulerInputFields.schedulingType = this.cachedSchedulingType;
                        this.deliveryScheduler.schedulingTypeChanged(this.cachedSchedulingType);
                    }

                    this.briefingChanged();
                });
                break;
        }
    }

    /**
     * Event handler for when the delivery component emits the deliveryDateChanged event, updates the sendAt accordingly
     */
    public deliveryDateChanged(event: Date): void {
        this.briefing.sendAt.setSendLater(event);
        // Trigger dirty checking
        this.briefingChanged();
    }

    /**
     * Event handler for when the delivery component emits the timeChange event, updates the sendAt accordingly
     */
    public timeChange(event: TimeModel): void {
        this.briefing.sendAt.timeZone = event?.timeZone;
        // Trigger dirty checking
        this.briefingChanged();
    }

    openEmailPreview(): void {
        const data = {
            fromName: this.briefing.fromName,
            briefingName: this.briefing.subject,
            inviteMessage: this.briefing.inviteMessage,
            onlineBriefingInviteStyle: this.briefing.onlineBriefingInviteStyle ?? null,
            isPreview: true,
        };
        this.templateApiService.getRendered('ONLINE_BRIEFING_INVITE', data)
            .subscribe({
                next: (html) => {
                    this.coreDialogService.open(EmailPreviewDialogComponent, {
                        inputs: { previewHtml: html }
                    });
                },
                error: (err) => {
                    this.messageService.add({
                        severity: this.messageSeverityEnum.error,
                        summary: this.localizationService.transform('Briefings.emailPreview.error.heading'),
                        detail: this.localizationService.transform('Briefings.emailPreview.error.message'),
                    });
                }
            });
    }


    private buildBrandStyle(briefing: BriefingModel): OnlineBriefingInviteStyleModel | null {
        const brand = briefing.canvasPreview?.brand;
        let brandColor;
        if (!brand) {
            const accentColor = briefing.generalStyles?.accentColor;
            if (accentColor) {
                brandColor = accentColor;
                return {brandColor, brandLogoUrl: '', brandName: 'Brand Name'}
            }
            return null;
        }

        brandColor = briefing.canvasPreview?.accentColor ?? '';
        const brandLogoUrl = briefing.canvasPreview?.logo?.[0]?.url ?? brand.logo?.source ?? '';
        const brandName = briefing.canvasPreview?.name ?? brand.name ?? '';

        if (this.brandStyle
            && this.brandStyle.brandColor === brandColor
            && this.brandStyle.brandLogoUrl === brandLogoUrl
            && this.brandStyle.brandName === brandName) {
            return this.brandStyle;
        }

        const result = { brandColor, brandLogoUrl, brandName };

        return result;
    }

    onlineBriefingInviteStyleChanged(style: OnlineBriefingInviteStyleModel): void {
        this.briefing.onlineBriefingInviteStyle = style;
        this.briefingChanged();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
